import { FC, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router';

import { useGetFAQQuery } from '../../api/queries/faq';
import { LoadingImages } from '../../components/LoadingImages/LoadingImages';
import { StylesheetContext } from '../../contexts/StylesheetContext';
import { useScrollToTop } from '../../helpers/hooks/useScrollToTop';
import { FAQCategory } from '../../interfaces/FAQ';
import FAQService from '../../services/FAQService';
import usePageTitle from '../../services/hooks/usePageTitle';
import { TicketOutletContext } from '../../services/LoaderService';
import styles from './FAQ.module.scss';
import { FAQSections } from './FAQSections';

const FAQ: FC = () => {
  const { stylesheet } = useContext(StylesheetContext);
  const { ticket } = useOutletContext<TicketOutletContext>();
  const { t } = useTranslation();
  const { faq, isFAQLoading } = useGetFAQQuery(ticket?.hashId);

  const thisUrlTranslation = t('shared:thisUrl');

  const populatedFaq = useMemo<FAQCategory[] | undefined>(() => {
    if (!ticket || !faq || isFAQLoading) {
      return undefined;
    }

    return FAQService.replaceMergeFields(faq, ticket, stylesheet, { thisUrl: thisUrlTranslation });
  }, [faq, ticket, stylesheet, thisUrlTranslation, isFAQLoading]);


  usePageTitle(t('pageTitle:faq'));
  useScrollToTop();

  if (isFAQLoading) {
    return (
      <LoadingImages
        items={[{ image: 'loadingQuestions', text: t('faq:loadingFAQ'), ariaLabel: t('faq:loadingFAQ') }]}
      />
    );
  }

  return (
    <div className={styles.faq}>
      {populatedFaq?.length ? (
        <FAQSections faqSections={populatedFaq} />
      ) : (
        <h2>{t('faq:thereAreNoFaqAvailable')}</h2>
      )}
    </div>
  );
};

export default FAQ;
